import { useQuery } from '@tanstack/react-query'

import { QUERY_KEYS } from '@/modules/shared/constants/query-keys.contant'
import { useAuthStore } from '@/modules/shared/stores/auth.store'

import { adminSellersListService } from '../services/admin-sellers-list.service'
import type { Seller } from '../services/admin-sellers-list.service'

type AdminSellersListQueryProps = {
  enabled?: boolean
}

export function useAdminSellersListQuery({
  enabled = true,
}: AdminSellersListQueryProps = {}) {
  const user = useAuthStore((state) => state.user)
  const versionUrl = useAuthStore((state) => state.versionUrl)
  const token = useAuthStore((state) => state.token)

  const {
    data: sellers = [],
    isInitialLoading: isLoadingSellers,
    error: sellersError,
    refetch: refetchSellers,
  } = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_SELLERS_LIST, versionUrl, user?.id, user?.vendedor],
    queryFn: async (): Promise<Seller[]> => {
      const response = await adminSellersListService()

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.message || 'Não foi possível carregar os vendedores.')
      }

      const sellers: unknown = JSON.parse(response.body)
      if (!Array.isArray(sellers) || !sellers.every((seller): seller is Seller =>
        seller !== null &&
        typeof seller === 'object' &&
        typeof seller.codigo === 'number' &&
        Number.isFinite(seller.codigo) &&
        typeof seller.vendedor === 'string' &&
        seller.vendedor.trim().length > 0
      )) {
        throw new Error('Resposta inválida ao buscar vendedores: esperada uma lista com código numérico e nome.')
      }

      return [...new Map(sellers.map((seller) => [seller.codigo, seller])).values()]
    },
    enabled: enabled && Boolean(token),
    staleTime: 1000 * 60 * 12,
    retry: 1,
    retryDelay: 1000,
  })

  return { sellers, isLoadingSellers, sellersError, refetchSellers }
}
